import { useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import qs from 'qs'

import { useDispatch } from '@tabeeb/store'
import { onAddErrorNotification } from '@tabeeb/modules/notification/actions'
import { ApiErrorResponse, UserResponse } from '@tabeeb/contracts/main-api/swagger-main-api'

import { useApi } from '@tabeeb/modules/shared/utils/API/hooks'
import getErrorMessageFromResponse from '@tabeeb/modules/shared/utils/getErrorMessageFromResponse'

import { QualificationsQueryKeys } from '../queries'
import type { QualificationVersion } from '../types'

export interface UseQualificationProps {
  qualificationId: number
  versionId: number
  onGetVersionError?: (error: AxiosError<ApiErrorResponse>) => void
}

const useQualification = ({ qualificationId, versionId, onGetVersionError }: UseQualificationProps) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const { certificateServiceApi: api, mainApi } = useApi()

  const versionsQuery = useQuery({
    queryKey: [QualificationsQueryKeys.QualificationVersions, qualificationId],
    queryFn: () => api.qualificationVersions.getVersionsAsync(qualificationId),
  })

  const versionQuery = useQuery({
    queryKey: [QualificationsQueryKeys.QualificationVersion, qualificationId, versionId],
    queryFn: async () => {
      const versionResponse = await api.qualificationVersions.getVersionAsync(qualificationId, versionId)

      const userGuids = new Set<string>()
      if (versionResponse.QualificationOwnerGuid) {
        userGuids.add(versionResponse.QualificationOwnerGuid)
      }
      if (versionResponse.VersionOwnerGuid) {
        userGuids.add(versionResponse.VersionOwnerGuid)
      }

      let users: UserResponse[] = []
      if (userGuids.size > 0) {
        const usersResponse = await mainApi.users.getUsersByIdentityGuidsAsync(
          {
            IdentityGuids: Array.from(userGuids),
          },
          {
            paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
          }
        )
        users = usersResponse ?? []
      }

      const versionWithUsers: QualificationVersion = {
        ...versionResponse,
        QualificationOwner: users.find((u) => u.IdentityGuid === versionResponse.QualificationOwnerGuid),
        VersionOwner: users.find((u) => u.IdentityGuid === versionResponse.VersionOwnerGuid),
      }

      return versionWithUsers
    },
    enabled: !!qualificationId && !!versionId,
  })

  const { isError: isVersionError, error: versionError } = versionQuery

  useEffect(() => {
    if (isVersionError && versionError) {
      const error = versionError as AxiosError<ApiErrorResponse>
      if (onGetVersionError) {
        onGetVersionError(error)
      }
    }
  }, [isVersionError, versionError, onGetVersionError])

  const updateQualificationMutation = useMutation({
    mutationFn: api.qualifications.updateAsync,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QualificationsQueryKeys.QualificationVersion] })
      queryClient.invalidateQueries({ queryKey: [QualificationsQueryKeys.Qualifications] })
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      const message = getErrorMessageFromResponse(error.response?.data)
      dispatch(onAddErrorNotification({ message }))
    },
  })

  const updateVersionMutation = useMutation({
    mutationFn: api.qualificationVersions.updateVersionsAsync,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QualificationsQueryKeys.QualificationVersion] })
      queryClient.invalidateQueries({ queryKey: [QualificationsQueryKeys.QualificationVersions] })
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      const message = getErrorMessageFromResponse(error.response?.data)
      dispatch(onAddErrorNotification({ message }))
    },
  })

  const createVersionMutation = useMutation({
    mutationFn: api.qualificationVersions.createVersionAsync,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QualificationsQueryKeys.QualificationVersions] })
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      const message = getErrorMessageFromResponse(error.response?.data)
      dispatch(onAddErrorNotification({ message }))
    },
  })

  const loading =
    versionsQuery.isLoading ||
    versionQuery.isLoading ||
    updateQualificationMutation.isPending ||
    updateVersionMutation.isPending ||
    createVersionMutation.isPending

  return useMemo(
    () => ({
      loading,
      versions: versionsQuery.data ?? [],
      version: versionQuery.data,
      onReloadVersion: versionQuery.refetch,
      onReloadVersionsList: versionsQuery.refetch,
      // mutations
      updateQualificationAsync: updateQualificationMutation.mutateAsync,
      updateVersionAsync: updateVersionMutation.mutateAsync,
      createVersionAsync: createVersionMutation.mutateAsync,
    }),
    [
      createVersionMutation.mutateAsync,
      loading,
      updateQualificationMutation.mutateAsync,
      updateVersionMutation.mutateAsync,
      versionQuery.data,
      versionQuery.refetch,
      versionsQuery.data,
      versionsQuery.refetch,
    ]
  )
}

export default useQualification
